import React, { useCallback, useContext, useEffect } from "react";
import { OmniboxSearchResult } from "common-client/generated/graphql";
import { useDebouncedCallback } from "common-client/utils/useDebouncedCallback";
import { useExecuteSearchLazyQuery } from "../../generated/graphql";
import { spacing } from "../../stitches.config";
import { FlexColumn } from "../Common/__styles__/Layout";
import Divider from "../Common/Divider";
import { Icon } from "../Common/Icons/LucideIcons";
import { Text } from "../Inputs";
import { InternalMapContext } from "../Maps/InternalMapContextProvider";
import { LayerContext } from "../Maps/layers";
import { SearchResultsContext } from "../Maps/SearchResultsContext";
import { SearchResultProps } from ".";
import {
  LeftContentWrapper,
  OmniboxSearchContainer,
  OmniboxSearchInputWrapper,
} from "./__styles__/OmniboxSearch";
import { OmniboxRecentSearches } from "./OmniboxRecentSearches";
import { OmniboxResults } from "./OmniboxResults";
import { useRecentSearches } from "./useRecentSearches";
import { searchResultCallbackProps } from "./utils";

const OmniboxSearchInput = () => {
  const { searchQueryState, searchQueryStateDispatch } =
    useContext(LayerContext);
  const { searchResultsDispatch } = useContext(SearchResultsContext);

  const [executeSearch] = useExecuteSearchLazyQuery({
    fetchPolicy: "network-only",
    onCompleted: ({ search }) => {
      searchResultsDispatch({ type: "searchComplete", results: search });
    },
  });

  const debouncedExecuteSearch = useDebouncedCallback(async (value: string) => {
    searchResultsDispatch({ type: "searchStarted", query: value });
    await executeSearch({ variables: { query: value } });
  }, 250);

  return (
    <OmniboxSearchInputWrapper>
      <LeftContentWrapper>
        <Icon iconName="search" color="contentSecondary" size="16" />
        <Text
          name="omnibox-search-input"
          data-testid="omnibox-search-input"
          placeholder="Search..."
          onChange={async value => {
            if (value) {
              searchQueryStateDispatch({ type: "searchEntered", query: value });
              await debouncedExecuteSearch(value);
            } else {
              searchQueryStateDispatch({ type: "searchCleared" });
              searchResultsDispatch({ type: "resetSearch" });
            }
          }}
          onFocus={() =>
            searchQueryStateDispatch({
              type: "setSearchPanel",
              searchPanelOpen: true,
            })
          }
          size="small"
          value={searchQueryState.query ?? ""}
          maxLength={256}
          style={{
            padding: 0,
            border: "none",
            height: "24px",
          }}
        />
      </LeftContentWrapper>
      {(searchQueryState.query || searchQueryState.searchPanelOpen) && (
        <Icon
          iconName="x"
          color="contentSecondary"
          size="16"
          data-testid="omnibox-search-clear"
          style={{ cursor: "pointer" }}
          onClick={() => {
            searchQueryStateDispatch({ type: "resetSearch" });
            searchResultsDispatch({ type: "resetSearch" });
          }}
        />
      )}
    </OmniboxSearchInputWrapper>
  );
};

export const OmniboxSearch = ({
  handleSearchResultClick,
  halfMap,
}: {
  halfMap: boolean;
  handleSearchResultClick: (searchResult: SearchResultProps) => void;
}) => {
  const { searchQueryState, searchQueryStateDispatch } =
    useContext(LayerContext);
  const { addRecentSearch } = useRecentSearches();
  const { searchResultsDispatch } = useContext(SearchResultsContext);
  // needed while we still have the old search around
  const { setResetSearchCategory } = useContext(InternalMapContext);
  useEffect(() => {
    if (setResetSearchCategory) {
      setResetSearchCategory(() => {
        searchQueryStateDispatch({ type: "resetSearch" });
        searchResultsDispatch({ type: "resetSearch" });
      });
    }
  }, [setResetSearchCategory, searchQueryStateDispatch, searchResultsDispatch]);

  const wrappedHandleSearchResultClick = useCallback(
    (result: OmniboxSearchResult) => {
      searchQueryStateDispatch({ type: "resetSearch" });
      searchResultsDispatch({ type: "resetSearch" });
      addRecentSearch(searchQueryState.query ?? "", result);
      handleSearchResultClick(searchResultCallbackProps(result));
    },
    [
      handleSearchResultClick,
      searchQueryStateDispatch,
      searchResultsDispatch,
      addRecentSearch,
      searchQueryState.query,
    ],
  );

  return (
    <OmniboxSearchContainer>
      <FlexColumn>
        <OmniboxSearchInput />
        {searchQueryState.searchPanelOpen &&
          (halfMap ?
            <Divider margins={`0 -${spacing.s.value}`} />
          : <Divider extension={16} />)}
      </FlexColumn>
      {searchQueryState.searchPanelOpen && !searchQueryState.query && (
        <OmniboxRecentSearches
          handleSearchResultClick={wrappedHandleSearchResultClick}
        />
      )}
      {searchQueryState.searchPanelOpen && searchQueryState.query && (
        <OmniboxResults
          handleSearchResultClick={wrappedHandleSearchResultClick}
        />
      )}
    </OmniboxSearchContainer>
  );
};
